import { useMemo } from 'react';
import { RoiType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { AnalyticsHooks, StatsMetricProps } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { featureFlags } from '../../../../feature-flags';
import { useFetchARROIOOverview } from '../../appointment-reminders/hooks/use-fetch-ar-roi-overview';
import { RoiSummary } from '../../components/roi-summary';
import { useNSRRoiShallowStore } from '../hooks/use-nsr-roi-store';
import { nsrTimePeriods } from './filters';

export const NSRSummary = () => {
  const { t } = useTranslation('analytics');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.noShowRateRoiDemoData);
  const { filters } = useNSRRoiShallowStore('filters');
  const timePeriod =
    filters?.startDate && filters?.endDate
      ? AnalyticsHooks.findExistingTimePeriodKey<typeof nsrTimePeriods>(
          filters.startDate,
          filters.endDate,
          nsrTimePeriods
        ) ?? undefined
      : undefined;

  const { aggregated, isLoading } = useFetchARROIOOverview({
    fetchNoShowRate: false,
    filters,
    forceDemoData: isDemoAccount,
    roiType: RoiType.WEAVE,
  });

  const locationsCount = filters?.locationIds?.length ?? 0;

  const summaryCardsData: StatsMetricProps[] = useMemo(() => {
    const totalAppointments = aggregated.totalAppointments;
    const totalAppointmentsCompleted = aggregated.appointmentsCompleted.totalAppointmentsCompleted;

    const values = {
      confirmedAppointmentsCount: aggregated.appointmentsConfirmed.appointmentsCount,
      confirmedPatientsCount: aggregated.appointmentsConfirmed.patientsCount,
      locationsCount,
      noShowRate: formatters.appendPercent(aggregated.noShowRatePercentage),
      timePeriodLabel: timePeriod ? nsrTimePeriods[timePeriod]?.label?.toLocaleLowerCase() : '',
      totalAppointments,
      totalAppointmentsCompleted,
      totalAppointmentsRemaining: totalAppointments - totalAppointmentsCompleted,
    };

    return [
      {
        uniqueId: 'no-show-rate',
        title: t('No-Show Rate'),
        data: [
          {
            label: t('{{totalAppointmentsCompleted}}/{{totalAppointments}} Completed', values),
            value: values.noShowRate,
            infoHoverText:
              locationsCount > 1
                ? t(
                    '{{totalAppointmentsCompleted}} out of {{totalAppointments}} appointments scheduled {{timePeriodLabel}} were completed across {{locationsCount}} locations. The remaining {{totalAppointmentsRemaining}} were no-shows, resulting in a {{noShowRate}} no-show rate',
                    values
                  )
                : t(
                    '{{totalAppointmentsCompleted}} out of {{totalAppointments}} appointments scheduled {{timePeriodLabel}} were completed. The remaining {{totalAppointmentsRemaining}} were no-shows, resulting in a {{noShowRate}} no-show rate',
                    values
                  ),
          },
        ],
      },
      {
        uniqueId: 'appointments-confirmed',
        title: t('Appointments Confirmed'),
        data: [
          {
            label: t('{{confirmedPatientsCount}} Patients', values),
            value: formatters.value(values.confirmedAppointmentsCount),
            infoHoverText:
              locationsCount > 1
                ? t(
                    'A total of {{confirmedAppointmentsCount}} appointments were confirmed by {{confirmedPatientsCount}} patients across {{locationsCount}} locations',
                    values
                  )
                : t(
                    'A total of {{confirmedAppointmentsCount}} appointments were confirmed by {{confirmedPatientsCount}} patients',
                    values
                  ),
          },
        ],
      },
    ];
  }, [aggregated, locationsCount, timePeriod, t]);

  return (
    <RoiSummary
      isDemoAccount={isDemoAccount}
      isLoading={isLoading}
      productionAppointmentsCount={aggregated.appointmentsCompleted.totalAppointmentsCompleted}
      productionPatientsCount={aggregated.appointmentsCompleted.appointmentsCompletedPatients}
      summaryCardsData={summaryCardsData}
    />
  );
};
